import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { EASE, IMG } from "@/lib/site";
import { CtaButton } from "./CtaButton";
import { Eyebrow } from "./Reveal";

const LINES = [["Move like"], ["your joints", true], ["remember."]];

const MaskLine = ({ text, italic, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${italic ? "font-serif text-[1.2em] leading-[0.9] italic font-normal text-[#D96B43]" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, ease: EASE, delay: 0.35 + i * 0.14 }}
    >
      {text}
    </motion.span>
  </span>
);

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-tilt-card">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="absolute -inset-10 rounded-full bg-[#D96B43]/25 blur-3xl" />
        <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] [clip-path:polygon(0_0,100%_0,100%_88%,82%_100%,0_100%)]">
          <img src={IMG.hero} alt="Two older hikers walking a mountain trail" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#091D18]/80 via-transparent to-transparent" />
        </div>
        <motion.img src="/joint-genesis-bottle.png" alt="Joint Genesis bottle by BioDynamix" data-testid="hero-product-bottle"
          style={{ transform: "translateZ(90px)" }}
          animate={{ y: [0, -12, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -right-4 -bottom-8 w-[62%] drop-shadow-[0_30px_40px_rgba(0,0,0,0.55)] sm:-right-10" />
        <div style={{ transform: "translateZ(60px)" }}
          className="absolute left-3 top-6 rounded-2xl border border-[#F2EDE4]/15 bg-[#091D18]/80 p-4 backdrop-blur-xl sm:-left-10 sm:top-10 sm:p-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E5A93C]">Featured pick</p>
          <p className="mt-1 font-display text-lg font-bold text-[#F2EDE4] sm:text-xl">Joint Genesis</p>
          <p className="text-xs text-[#F2EDE4]/70 sm:text-sm">Daily joint-support formula</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const big = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-[#091D18] pb-28 pt-32 sm:pt-40 lg:min-h-screen" data-testid="hero-section">
      <div className="grain" />
      <motion.p style={{ x: big }} aria-hidden="true"
        className="pointer-events-none absolute -bottom-6 left-0 whitespace-nowrap font-display text-[28vw] font-extrabold leading-none text-[#F2EDE4]/[0.04]">
        gobbloo gobbloo
      </motion.p>
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}>
            <Eyebrow>Gobbloo Review — No. 01 · Joint Health</Eyebrow>
          </motion.div>
          <h1 className="mt-6 font-display text-[2.4rem] font-bold leading-[1] tracking-tight text-[#F2EDE4] sm:text-6xl lg:text-[4.6rem] xl:text-[5.2rem]" data-testid="hero-headline">
            {LINES.map(([t, it], i) => <MaskLine key={t} text={t} italic={it} i={i} />)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: EASE }}
            className="mt-8 max-w-lg text-base leading-relaxed text-[#F2EDE4]/75 sm:text-lg">
            Our close look at <span className="text-[#F2EDE4]">Joint Genesis</span> — a daily supplement built around Mobilee®,
            a hyaluronic-acid matrix aimed at the cushioning fluid inside your joints.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <CtaButton testId="hero-hoplink-button" placement="hero">Check official price</CtaButton>
            <a href="#science" data-testid="hero-learn-more" className="text-sm text-[#F2EDE4]/75 underline-offset-4 hover:text-[#F2EDE4] hover:underline">
              Read the science first
            </a>
          </motion.div>
        </div>
        <motion.div style={{ y }} className="mx-auto w-full max-w-md lg:col-span-5"
          initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}>
          <TiltCard />
        </motion.div>
      </div>
    </section>
  );
};
